import { createSlice, PayloadAction } from "@reduxjs/toolkit";
// register and login both time save token
interface B2BInfo {
  persona: string;
  industry: string;
  difficulty: string;
  other_info: string;
}

interface B2CInfo {
  customer: string;
  age: string;
  gender: string;
  motivation: string;
  difficulty: string;
  income: string;
  other_info: string;
}

interface IaboutYou {
  name: string;
  role: string;
}

interface IaboutCompany {
  name: string;
  companyName: string;
  jobTitle: string;
}

interface ISubscription {
  subscriptionId: string;
  stripeId: string;
  ownerId: string;
  startDate: string;
  endDate: string;
  planType: string;
  seats: number;
  duration: string;
  // "stripe", "free" or the trial provider - only set for plans the user owns.
  paymentProvider?: string;
  isOwner?: boolean | undefined | null;
  status: string;
}

// Free-trial state, mirrored from GET /me. Only meaningful while payments are
// switched off - see STRIPE_ENABLED / TRIAL_DAYS on the backend.
interface ITrial {
  isTrial: boolean;
  daysLeft: number;
  endDate: string | null;
  trialDays: number;
  canStartTrial: boolean;
}

export const emptyTrial: ITrial = {
  isTrial: false,
  daysLeft: 0,
  endDate: null,
  trialDays: 0,
  canStartTrial: false,
};

// The plan that most recently ended, mirrored from GET /me. Only set while the
// user has no access, so the app can say why: a trial vs a paid plan, and
// whether it was the user's own plan or their team owner's.
export interface IExpiredPlan {
  isTrial: boolean;
  isTeamMember: boolean;
  planType: string;
  endDate: string;
  ownerEmail: string | null;
}

interface InitialStateTypes {
  user: {
    email: string;
    role: string;
    accessToken: string;
    isVerified: boolean;
    id: string;
    jobProfileRole: string;
    aboutYou: IaboutYou;
    aboutCompany: IaboutCompany;
    subscription: ISubscription;
    trial: ITrial;
    expiredPlan: IExpiredPlan | null;
    notifications: number;
  };
  info: {
    industry: string;
    product: string;
    targetBuyer: string;
    b2b?: B2BInfo;
    b2c?: B2CInfo;
  };
  final: {
    isProcessing: boolean;
    disableStartPitching: boolean;
    countdown: number;
    showIProcessingText: boolean;
  };
  feedbackStates: {
    voice: string;
  };
  token: {
    access: string;
  };
  sideBar: {
    isSidebarOpen: boolean;
  };
  paymentIntent: {
    duration: string;
    price: number;
    seats: number;
    name: string;
    planType: string;
    perSeatPerDay: number;
    remainingDays: number;
  };
  config: {
    stripeEnabled: boolean;
  };
}

const initialState: InitialStateTypes = {
  user: {
    email: "",
    role: "",
    id: "",
    isVerified: false,
    accessToken: "",
    jobProfileRole: "",
    notifications: 0,
    aboutYou: {
      name: "",
      role: "",
    },
    aboutCompany: {
      name: "",
      companyName: "",
      jobTitle: "",
    },
    subscription: {
      subscriptionId: "",
      stripeId: "",
      ownerId: "",
      planType: "",
      seats: 0,
      startDate: "",
      endDate: "",
      duration: "",
      isOwner: false,
      status: "",
    },
    trial: emptyTrial,
    expiredPlan: null,
  },
  info: {
    industry: "",
    product: "",
    targetBuyer: "",
    b2b: {
      persona: "",
      industry: "",
      difficulty: "",
      other_info: "",
    },
    b2c: {
      customer: "",
      age: "",
      gender: "",
      motivation: "",
      difficulty: "",
      income: "",
      other_info: "",
    },
  },
  final: {
    isProcessing: false,
    disableStartPitching: false,
    countdown: -1,
    showIProcessingText: false,
  },
  feedbackStates: {
    voice: "ash",
  },
  token: {
    access: "",
  },
  sideBar: {
    isSidebarOpen: false,
  },
  paymentIntent: {
    duration: "",
    price: 0,
    name: "",
    planType: "",
    seats: 0,
    perSeatPerDay: 0,
    remainingDays: 0,
  },
  config: {
    stripeEnabled: true,
  },
};

export const globalSlice = createSlice({
  name: "global",
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<any>) => {
      state.user = {
        ...state.user,
        ...action.payload,
      };
    },
    setToken: (state, action: PayloadAction<any>) => {
      state.token = {
        ...state.token,
        ...action.payload,
      };
    },
    logout: (state) => {
      state.user = {
        email: "",
        role: "",
        accessToken: "",
        isVerified: false,
        id: "",
        notifications: 0,
        jobProfileRole: "",
        aboutYou: {
          name: "",
          role: "",
        },
        aboutCompany: {
          name: "",
          companyName: "",
          jobTitle: "",
        },
        subscription: {
          subscriptionId: "",
          stripeId: "",
          ownerId: "",
          startDate: "",
          endDate: "",
          planType: "",
          seats: 0,
          duration: "",
          isOwner: false,
          status: "",
        },
        trial: emptyTrial,
        expiredPlan: null,
      };
    },
    setInfo: (state, action: PayloadAction<Partial<any>>) => {
      state.info = {
        ...state.info,
        ...action.payload,
      };
    },
    setFinal: (state, action: PayloadAction<Partial<any>>) => {
      state.final = {
        ...state.final,
        ...action.payload,
      };
    },
    setFeedbackStates: (state, action: PayloadAction<Partial<any>>) => {
      state.feedbackStates = {
        ...state.feedbackStates,
        ...action.payload,
      };
    },
    setIsSidebarOpen: (state, action: PayloadAction<Partial<any>>) => {
      state.sideBar = {
        ...state.sideBar,
        ...action.payload,
      };
    },
    setPaymentIntent: (state, action: PayloadAction<Partial<any>>) => {
      state.paymentIntent = {
        ...state.paymentIntent,
        ...action.payload,
      };
    },
    setConfig: (state, action: PayloadAction<Partial<any>>) => {
      state.config = {
        ...state.config,
        ...action.payload,
      };
    },
  },
});

export const {
  setUser,
  logout,
  setInfo,
  setFinal,
  setFeedbackStates,
  setToken,
  setIsSidebarOpen,
  setPaymentIntent,
  setConfig,
} = globalSlice.actions;

export default globalSlice.reducer;
